import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Crown, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 12) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refreshUser]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        {status === "checking" && (<><Loader2 className="mx-auto h-10 w-10 animate-spin text-[#18A6A6]" /><h1 className="mt-4 font-heading text-2xl font-bold text-[#0F2747]">Confirming your payment…</h1><p className="mt-2 text-slate-500">This only takes a moment.</p></>)}
        {status === "paid" && (<>
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#D6A84B]/15"><Crown className="h-7 w-7 text-[#D6A84B]" /></div>
          <h1 className="mt-4 font-heading text-2xl font-bold text-[#0F2747]">Welcome to Pro! 🎉</h1>
          <p className="mt-2 text-slate-500">Your RN Level Up Pro membership is active. Enjoy unlimited tools.</p>
          <Button data-testid="success-dashboard-btn" onClick={() => navigate("/dashboard")} className="mt-6 w-full rounded-full bg-[#18A6A6] py-6 hover:bg-[#148f8f]">Go to dashboard</Button>
        </>)}
        {["failed", "error", "timeout"].includes(status) && (<>
          <XCircle className="mx-auto h-10 w-10 text-red-500" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-[#0F2747]">Payment not confirmed</h1>
          <p className="mt-2 text-slate-500">{status === "timeout" ? "It's taking longer than expected. If you were charged, your access will unlock shortly." : "We couldn't confirm your payment."}</p>
          <Button data-testid="retry-upgrade-btn" onClick={() => navigate("/upgrade")} className="mt-6 w-full rounded-full bg-[#0F2747] py-6 hover:bg-[#16355F]">Back to upgrade</Button>
        </>)}
      </div>
    </div>
  );
}
